import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useViewerRoles } from '@treams/treams-hooks';
import { ChevronRightIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Avatar, Heading3, Paragraph, Skeleton, tokens } from '@treams/treams-ui';

const Chevron = styled(Box)`
	width: 24px;

	svg {
		display: block;

		path {
			fill: ${tokens.text.secondary};
		}
	}
`;

type MemberEdge = graphql.GroupMembersQuery['group']['members']['edges'][number];

export default function Preview(props: { group: graphql.Group }) {
	const { t } = useTranslation();

	const inlineEdges = (props.group as unknown as { members?: { edges?: MemberEdge[] } }).members
		?.edges;
	const hasInlineMembers = inlineEdges !== undefined;

	const query = graphql.useGroupMembersQuery({
		variables: { id: props.group.id },
		skip: hasInlineMembers,
	});
	const { isWorkspaceSuperuser, isWorkspaceAdmin } = useViewerRoles();

	const edges = inlineEdges ?? query.data?.group.members.edges;
	const loaded = hasInlineMembers || query.data !== undefined || query.error !== undefined;

	const managers = (edges ?? []).filter((edge) => edge.role.type !== graphql.RoleType.Member);
	const members = (edges ?? []).filter((edge) => edge.role.type === graphql.RoleType.Member);

	return (
		<Box p={3}>
			<Flex>
				<Heading3 mb={0}>{props.group.name}</Heading3>
				{(isWorkspaceSuperuser === true || isWorkspaceAdmin === true) && (
					<Link to={`/v2/settings/organization/departments/${props.group.id}`}>
						<Chevron>
							<ChevronRightIcon />
						</Chevron>
					</Link>
				)}
			</Flex>
			<Box mt={2}>
				<Paragraph>{t('groups.selector.preview.managers.ttile')}</Paragraph>
				{loaded ? (
					managers.length === 0 ? (
						<Flex>
							<Skeleton.Empty
								contents={
									<Paragraph textAlign="center" style={{ whiteSpace: 'pre' }}>
										{t('groups.selector.preview.managers.empty')}
									</Paragraph>
								}
								mt={1}
							>
								<Flex>
									<Skeleton.Repeat count={5}>
										<Avatar.Skeleton size={32} mr={2} />
									</Skeleton.Repeat>
								</Flex>
							</Skeleton.Empty>
						</Flex>
					) : (
						<Flex mt={1}>
							{managers.slice(0, 4).map(({ node }) => (
								<Avatar key={node.id} user={node as graphql.User} mr={2} size={32} />
							))}
							{managers.length > 4 && <Avatar.Overflow count={managers.length - 4} size={32} />}
						</Flex>
					)
				) : (
					<Flex mt={1}>
						<Avatar.Skeleton mr={2} size={32} />
						<Avatar.Skeleton mr={2} size={32} />
						<Avatar.Skeleton mr={2} size={32} />
						<Avatar.Skeleton mr={2} size={32} />
						<Avatar.Skeleton mr={2} size={32} />
					</Flex>
				)}
			</Box>
			<Box mt={3}>
				<Paragraph>{t('groups.selector.preview.employees.title')}</Paragraph>
				{loaded ? (
					members.length === 0 ? (
						<Flex>
							<Skeleton.Empty
								contents={
									<Paragraph textAlign="center" style={{ whiteSpace: 'pre' }}>
										{t('groups.selector.preview.employees.empty')}
									</Paragraph>
								}
								mt={1}
							>
								<Flex>
									<Skeleton.Repeat count={5}>
										<Avatar.Skeleton size={32} mr={2} />
									</Skeleton.Repeat>
								</Flex>
							</Skeleton.Empty>
						</Flex>
					) : (
						<Flex mt={1}>
							{members.slice(0, 4).map(({ node }) => (
								<Avatar key={node.id} user={node as graphql.User} mr={2} size={32} />
							))}
							{members.length > 4 && <Avatar.Overflow count={members.length - 4} size={32} />}
						</Flex>
					)
				) : (
					<Flex mt={1}>
						<Avatar.Skeleton mr={2} size={32} />
						<Avatar.Skeleton mr={2} size={32} />
						<Avatar.Skeleton mr={2} size={32} />
						<Avatar.Skeleton mr={2} size={32} />
						<Avatar.Skeleton mr={2} size={32} />
					</Flex>
				)}
			</Box>
		</Box>
	);
}
